import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Button, TextField,
    Stack, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, InputAdornment, MenuItem, Divider
} from "@mui/material";
import { Add, Edit, Delete, Search, DirectionsBus, CloudUpload } from "@mui/icons-material";
import { collection, getDocs, doc, setDoc, deleteDoc, query, where, orderBy, writeBatch } from "firebase/firestore";
import { db } from "../../firebase";

function Locations() {
    const [locations, setLocations] = useState([]);
    const [locationTypes, setLocationTypes] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    
    const [open, setOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);

    // Form States
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({ locationName: "", locationType: "", km: "" });
    const [bulkData, setBulkData] = useState("");
    const [bulkType, setBulkType] = useState("");

    const schoolId = "zIlbJM5BjyrlEhg7fWKs";

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [locSnap, typeSnap] = await Promise.all([
                getDocs(query(collection(db, "locations"), where("schoolId", "==", schoolId))),
                getDocs(query(collection(db, "location_types"), where("schoolId", "==", schoolId), orderBy('fee', 'asc')))
            ]);

            setLocations(locSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLocationTypes(typeSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) { console.error(e); }
    };

    const handleSave = async () => {
        if (!formData.locationName || !formData.locationType) return;
        const docId = editId || doc(collection(db, "locations")).id;
        await setDoc(doc(db, "locations", docId), {
            ...formData,
            km: formData.km === "" ? null : parseFloat(formData.km),
            schoolId,
            updatedAt: new Date().toISOString()
        });
        setOpen(false);
        loadData();
    };

    const handleBulkImport = async () => {
        if (!bulkType || !bulkData) return;
        const batch = writeBatch(db);
        
        // Format: "Location1, 5; Location2, 10; Location3"
        const entries = bulkData.split(";");
        
        entries.forEach(entry => {
            const [name, km] = entry.split(",");
            if (name && name.trim()) {
                const newDocRef = doc(collection(db, "locations"));
                batch.set(newDocRef, {
                    locationName: name.trim(),
                    locationType: bulkType,
                    km: km ? parseFloat(km.trim()) : null,
                    schoolId,
                    updatedAt: new Date().toISOString()
                });
            }
        });

        await batch.commit();
        setBulkOpen(false);
        setBulkData("");
        loadData();
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete location?")) {
            await deleteDoc(doc(db, "locations", id));
            loadData();
        }
    };

    // Grouping Logic
    const groupedLocations = locationTypes.map(type => ({
        ...type,
        items: locations.filter(loc => 
            loc.locationType === type.name && 
            loc.locationName.toLowerCase().includes(searchTerm.toLowerCase())
        )
    })).filter(group => group.items.length > 0 || searchTerm === "");

    return (
        <Box p={2}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold"><DirectionsBus color="primary" /> Bus Routes</Typography>
                    <Stack direction="row" spacing={1}>
                        <TextField 
                            placeholder="Search..." size="small" value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        />
                        {/* <Button variant="outlined" startIcon={<CloudUpload />} onClick={() => setBulkOpen(true)}>Import</Button> */}
                        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditId(null); setOpen(true); }}>Add</Button>
                    </Stack>
                </Stack>
            </Paper>

            {groupedLocations.map((group) => (
                <Box key={group.id} mb={4}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {group.name} <Typography variant="caption" color="text.secondary">(Fee: ₹{group.fee})</Typography>
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#fcfcfc' }}>
                                <TableRow>
                                    <TableCell width={60}>Sr.</TableCell>
                                    <TableCell>Location Name</TableCell>
                                    <TableCell>Distance</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group.items.map((loc, i) => (
                                    <TableRow key={loc.id} hover>
                                        <TableCell>{i + 1}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{loc.locationName}</TableCell>
                                        <TableCell>{loc.km ? `${loc.km} KM` : "N/A"}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => { setEditId(loc.id); setFormData(loc); setOpen(true); }}><Edit fontSize="small" color="primary" /></IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(loc.id)}><Delete fontSize="small" color="error" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            ))}

            {/* Single Add/Edit Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>{editId ? "Edit" : "Add"} Location</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} mt={1}>
                        <TextField label="Name" fullWidth size="small" value={formData.locationName} onChange={e => setFormData({ ...formData, locationName: e.target.value })} />
                        <TextField select label="Type" fullWidth size="small" value={formData.locationType} onChange={e => setFormData({ ...formData, locationType: e.target.value })}>
                            {locationTypes.map(t => <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>)}
                        </TextField>
                        <TextField label="KM" type="number" fullWidth size="small" value={formData.km} onChange={e => setFormData({ ...formData, km: e.target.value })} />
                    </Stack>
                </DialogContent>
                <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleSave}>Save</Button></DialogActions>
            </Dialog>

            {/* Bulk Import Dialog */}
            <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Bulk Import Locations</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="caption" color="error" gutterBottom>Format: Location Name, KM; Next Location, KM; (Use semicolon to separate locations)</Typography>
                    <TextField select label="Select Group/Type" fullWidth size="small" sx={{ my: 2 }} value={bulkType} onChange={e => setBulkType(e.target.value)}>
                        {locationTypes.map(t => <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>)}
                    </TextField>
                    <TextField label="Comma Separated Data" multiline rows={6} fullWidth placeholder="Main Square, 2; Bus Stand, 4; Sector 10" value={bulkData} onChange={e => setBulkData(e.target.value)} />
                </DialogContent>
                <DialogActions><Button onClick={() => setBulkOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleBulkImport}>Import All</Button></DialogActions>
            </Dialog>
        </Box>
    );
}

export default Locations;